'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListWatchConfig } from '@/lib/backend-api';
import { throwIfInsufficientCredits } from '@/hooks/use-alerts';
import { useSession } from '@/hooks/use-session';

/**
 * One company's alert configuration — the same shape a watched list has.
 *
 * A company watch and a list watch are one subscription kind since the watch
 * layer merged them; this hook is the company half of what `useListDetail`
 * exposes for lists. `null` means the company is not watched at all (the
 * endpoint 404s), which the caller shows as "no alerts configured" rather
 * than an error.
 */
export function useCompanyWatch(euId: string) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<ListWatchConfig | null>({
    queryKey: ['watch', 'company', euId],
    queryFn: async () => {
      const res = await fetch(
        `/api/watch/company/${encodeURIComponent(euId)}`,
      );
      // Not watched is the ordinary state for most companies, so it must not
      // land in the rejected path and retry.
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`company watch returned ${res.status}`);
      const json = await res.json();
      return json.data as ListWatchConfig;
    },
    enabled: !!session && !!euId,
    staleTime: 60 * 1000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['watch', 'company', euId] });
    // The star button and the header badge read the membership list, which a
    // save can change (configuring an unwatched company starts watching it).
    void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    void queryClient.invalidateQueries({ queryKey: ['watch'] });
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (config: ListWatchConfig): Promise<void> => {
      const res = await fetch(
        `/api/watch/company/${encodeURIComponent(euId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(config),
        },
      );
      // A 402 (alert cannot be prepaid) throws InsufficientCreditsError,
      // which the wizard turns into the top-up prompt.
      await throwIfInsufficientCredits(res);
      if (!res.ok) throw new Error(`company watch PUT returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetch(
        `/api/watch/company/${encodeURIComponent(euId)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error(`company watch DELETE returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return {
    watch: data ?? null,
    isLoading,
    save: (config: ListWatchConfig) => saveMutation.mutateAsync(config),
    remove: () => removeMutation.mutateAsync(),
    isBusy: saveMutation.isPending || removeMutation.isPending,
  };
}
